import { useState } from 'react';
import { BarChart3 } from 'lucide-react';
import type {
  ReferralCohortCounts,
  ReferralCohortGroupBy,
  ReferralCohortParams,
} from '../api/adminOps';
import { useReferralCohorts } from '../hooks/useAdminOps';
import {
  activationCell,
  cohortRangeBlocker,
  defaultCohortRange,
  definitionsNote,
  retentionCell,
} from './referralCodes.logic';

function CountCells({ counts }: { counts: ReferralCohortCounts }) {
  return (
    <>
      <td className="px-3 py-2 tabular-nums">{counts.verified_signups.toLocaleString()}</td>
      <td className="px-3 py-2 tabular-nums">{activationCell(counts)}</td>
      <td className="px-3 py-2 tabular-nums">{retentionCell(counts)}</td>
      <td className="px-3 py-2 tabular-nums">{counts.deleted_accounts.toLocaleString()}</td>
    </>
  );
}

export default function ReferralCohortReport() {
  const initial = defaultCohortRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [partnerName, setPartnerName] = useState('');
  const [groupBy, setGroupBy] = useState<ReferralCohortGroupBy>('partner');

  const blocker = cohortRangeBlocker(from, to);
  const params: ReferralCohortParams = {
    from,
    to,
    group_by: groupBy,
    ...(partnerName.trim().length > 0 ? { partner_name: partnerName.trim() } : {}),
  };
  const report = useReferralCohorts(params, blocker === null);
  const data = report.data;

  return (
    <section className="glass-card p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <BarChart3 className="w-5 h-5" /> Founding Partner cohorts
        </h2>
        <p className="text-gray-text text-sm mt-1">
          Aggregate counts only. No donor is identified in this report.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor="cohort-from">
            From
          </label>
          <input
            id="cohort-from"
            type="date"
            className="input"
            value={from}
            onChange={event => setFrom(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="cohort-to">
            To
          </label>
          <input
            id="cohort-to"
            type="date"
            className="input"
            value={to}
            onChange={event => setTo(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="cohort-partner">
            Partner
          </label>
          <input
            id="cohort-partner"
            className="input"
            value={partnerName}
            onChange={event => setPartnerName(event.target.value)}
            placeholder="All partners"
          />
        </div>
        <div>
          <label className="label" htmlFor="cohort-group">
            Group by
          </label>
          <select
            id="cohort-group"
            className="input"
            value={groupBy}
            onChange={event => setGroupBy(event.target.value as ReferralCohortGroupBy)}
          >
            <option value="partner">Partner</option>
            <option value="code">Code</option>
          </select>
        </div>
      </div>

      {blocker !== null && <p className="text-sm text-yellow-400">{blocker}</p>}
      {report.isError && (
        <div className="space-y-2">
          <p className="text-sm text-red-400">
            Could not load the report: {(report.error as Error).message}
          </p>
          <button className="btn-secondary" onClick={() => report.refetch()}>
            Retry
          </button>
        </div>
      )}
      {blocker === null && report.isPending && (
        <p className="text-sm text-gray-text">Loading cohorts…</p>
      )}

      {data && (
        <>
          {data.rows.length === 0 ? (
            <p className="text-sm text-gray-text">
              No verified signups between {data.from} and {data.to}.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-text">
                    <th className="px-3 py-2">Partner</th>
                    {data.group_by === 'code' && <th className="px-3 py-2">Code</th>}
                    <th className="px-3 py-2">Verified signups</th>
                    <th className="px-3 py-2">Activated</th>
                    <th className="px-3 py-2">Retained</th>
                    <th className="px-3 py-2">Deleted accounts</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(row => (
                    <tr
                      key={`${row.partner_name}:${row.code_id ?? ''}`}
                      className="border-t border-gray-border"
                    >
                      <td className="px-3 py-2">{row.partner_name}</td>
                      {data.group_by === 'code' && (
                        <td className="px-3 py-2 font-mono">{row.code ?? '—'}</td>
                      )}
                      <CountCells counts={row} />
                    </tr>
                  ))}
                  <tr className="border-t border-gray-border font-semibold">
                    <td className="px-3 py-2">Total</td>
                    {data.group_by === 'code' && <td className="px-3 py-2" />}
                    <CountCells counts={data.totals} />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-gray-text">{definitionsNote(data.definitions)}</p>
          <p className="text-xs text-gray-text">
            As of {new Date(data.as_of).toLocaleString()}. Signups are dated by their first
            successful sign-in.
          </p>
        </>
      )}
    </section>
  );
}
